import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import { forwardRef, useContext, useMemo } from 'react';
import { Dropdown as BaseDropdown } from '@restart/ui';
import { useUncontrolled } from 'uncontrollable';
import useEventCallback from '@restart/hooks/useEventCallback';
import SelectableContext from '../SelectableContext';
import { DropdownMenu } from './DropdownMenu';
import { DropdownToggle } from './DropdownToggle';
const getDropdownClass = (placement)=>{
    if (placement) {
        if (placement.startsWith('top')) return 'dropup';
        if (placement.startsWith('right')) return 'dropend';
        if (placement.startsWith('left')) return 'dropstart';
    }
    return 'dropdown';
};
// eslint-disable-next-line react/display-name
export const Dropdown = /*#__PURE__*/ forwardRef((pProps, ref)=>{
    const { drop = 'down', align = 'start', show, className, onSelect, onToggle, focusFirstItemOnShow, componentClass: Component = 'div', ...props } = useUncontrolled(pProps, {
        show: 'onToggle'
    });
    const placement = useMemo(()=>{
        const directions = {
            up: 'top',
            down: 'bottom',
            start: 'left',
            end: 'right'
        };
        const dir = drop ? directions[drop] : undefined;
        if (!dir) return undefined;
        const p = align === 'start' || align === 'end' ? `${dir}-${align}` : dir;
        return p;
    }, [
        drop,
        align
    ]);
    const dropdownClass = useMemo(()=>getDropdownClass(placement), [
        placement
    ]);
    const onSelectCtx = useContext(SelectableContext);
    const prefix = 'dropdown';
    const handleToggle = useEventCallback((nextShow, event, source = event.type, key = undefined)=>{
        let src = source;
        if (event.currentTarget === document && (source !== 'keydown' || event.key === 'Escape')) src = 'rootClose';
        if (onToggle) {
            onToggle(nextShow, event, {
                source: src,
                key
            });
        }
    });
    const handleSelect = useEventCallback((key, event)=>{
        if (onSelectCtx) onSelectCtx(key, event);
        if (onSelect) onSelect(key, event);
        handleToggle(false, event, 'select', key);
    });
    return /*#__PURE__*/ _jsx(SelectableContext.Provider, {
        value: handleSelect,
        children: /*#__PURE__*/ _jsx(BaseDropdown, {
            placement: placement,
            show: show,
            onToggle: handleToggle,
            focusFirstItemOnShow: focusFirstItemOnShow,
            itemSelector: `.${prefix}-item:not(.disabled):not(:disabled)`,
            children: /*#__PURE__*/ _jsx(Component, {
                ...props,
                ref: ref,
                className: classNames(className, show && 'show', dropdownClass)
            })
        })
    });
});
Dropdown.displayName = 'Dropdown';
Dropdown.Menu = DropdownMenu;
Dropdown.Toggle = DropdownToggle;
